import { CardCvcElement, CardExpiryElement, CardNumberElement, Elements } from '@stripe/react-stripe-js';
import { useEffect } from 'react';
import { FormProvider, useForm, useFormContext } from 'react-hook-form';
import { AlertBox } from 'shared/ui/AlertBox';
import { Button } from 'shared/ui/Button';
import { Input } from 'shared/ui/Input';
import { FormSelect } from 'shared/ui/Select/Select';
import { AlertModal, SideModal, SideModalBody, useModalsActions } from 'shared/ui/SideModal';
import { getDataAutoTestAttributes } from 'shared/lib/data-auto-test';

import { CardInputWrapper } from './CardInputWrapper';
import { StripeOptions, useStripePromise } from './Stripe';
import { useAddCard } from './useAddCard';
import {
  cardCvc,
  cardExpiry,
  cardNumber,
  countries,
  defaultValues,
  inputAttributes,
  requiredMessage,
} from './settings';

export function FormFields({ onAdded }: { onAdded: () => void }) {
  const { submit, isLoading, isSuccess } = useAddCard();
  const { close, closeAll } = useModalsActions();
  const {
    handleSubmit,
    formState: { isDirty },
  } = useFormContext();

  useEffect(() => {
    if (isSuccess) {
      closeAll();
      onAdded();
    }
  }, [isSuccess]);

  return (
    <SideModal autoTestAttributesName="add-credit-card-modal">
      <SideModalBody title="Add credit card">
        <form
          className="w-85 pb-8"
          onSubmit={handleSubmit(submit)}
        >
          <AlertModal
            autoTestAttributesName="add-credit-card-exit-modal"
            showCondition={isDirty}
          />
          <div className="mb-2 text-sm font-medium">Card Details</div>

          <CardInputWrapper
            label="Card Number"
            type={cardNumber}
            {...getDataAutoTestAttributes(['input-card-number'])}
          >
            {(params) => <CardNumberElement {...params} />}
          </CardInputWrapper>

          <div className="flex gap-2">
            <div className="flex-1">
              <CardInputWrapper
                label="Expire Date"
                type={cardExpiry}
                {...getDataAutoTestAttributes(['input-card-expire-date'])}
              >
                {(params) => <CardExpiryElement {...params} />}
              </CardInputWrapper>
            </div>
            <div className="flex-1">
              <CardInputWrapper
                label="CVC / CVV"
                type={cardCvc}
                {...getDataAutoTestAttributes(['input-card-cvc-cvv'])}
              >
                {(params) => <CardCvcElement {...params} />}
              </CardInputWrapper>
            </div>
          </div>

          <Input
            className="mb-2"
            name="name"
            title="Name on Card"
            registerOptions={{ required: requiredMessage }}
            attributes={{ ...getDataAutoTestAttributes(['input-name-on-card']).attributes, ...inputAttributes }}
          />
          <div className="mb-2 mt-4 text-sm font-medium">Billing Details</div>
          <Input
            className="mb-2"
            name="line1"
            title="Address Line 1"
            registerOptions={{ required: requiredMessage }}
            attributes={{ ...getDataAutoTestAttributes(['input-address-line-1']).attributes, ...inputAttributes }}
          />
          <Input
            isOptional
            className="mb-2"
            name="line2"
            title="Address Line 2"
            attributes={{ ...getDataAutoTestAttributes(['input-address-line-2']).attributes, ...inputAttributes }}
          />
          <Input
            className="mb-2"
            name="city"
            title="City"
            registerOptions={{ required: requiredMessage }}
            attributes={{ ...getDataAutoTestAttributes(['input-city']).attributes, ...inputAttributes }}
          />
          <div className="flex gap-2">
            <Input
              className="mb-2"
              name="state"
              title="State"
              registerOptions={{ required: requiredMessage }}
              attributes={{
                ...getDataAutoTestAttributes(['input-state-province']).attributes,
                ...inputAttributes,
              }}
            />
            <Input
              className="mb-2"
              name="postal_code"
              title="ZIP"
              registerOptions={{ required: requiredMessage }}
              {...getDataAutoTestAttributes(['input-postal-code'])}
            />
          </div>
          <FormSelect
            isRequired
            title="Country"
            name="country"
            options={countries}
            selectedOptionId="US"
            {...getDataAutoTestAttributes(['select-country'])}
          />
          <AlertBox
            className="mb-6 mt-4 !items-start border-deep-blue-100"
            iconClass="text-deep-blue-500"
            iconName="info"
          >
            <div className="text-sm">
              <div className="font-medium">You wont be charged right now</div>
              <div className="mt-2 text-slate-700">
                Your card will be charged once you begin receiving leads and/or appointments.
              </div>
            </div>
          </AlertBox>
          <div className="flex gap-4">
            <Button
              className="w-full"
              onClick={close}
              type="button"
              variant="outline"
              disabled={isLoading}
              {...getDataAutoTestAttributes(['button-card-cancel'])}
            >
              Cancel
            </Button>
            <Button
              className="w-[222px] shrink-0"
              loading={isLoading}
              iconLeftName="lock"
              {...getDataAutoTestAttributes(['button-card-add'])}
            >
              Add card
            </Button>
          </div>
        </form>
      </SideModalBody>
    </SideModal>
  );
}

export function AddCardFormModal({ onAdded }: { onAdded: () => void }) {
  const methods = useForm({ defaultValues });
  const stripePromise = useStripePromise();

  return (
    <Elements
      stripe={stripePromise}
      options={StripeOptions}
    >
      <FormProvider {...methods}>
        <FormFields onAdded={onAdded} />
      </FormProvider>
    </Elements>
  );
}
